import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle, MessageCircle } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { useSiteSettings } from "../context/SiteSettingsContext";
import { formatJOD } from "../components/ProductCard";

/**
 * Build a WhatsApp pre-filled message containing the full order.
 * The customer taps "Send order to WhatsApp" → opens wa.me with this text
 * already typed in → they press send and the store receives the details.
 */
const buildWaMessage = (order, t) => {
  if (!order) return "";
  const shortId = order.id.slice(0, 8).toUpperCase();
  const lines = [];
  lines.push(`*${t("orderSuccess.waMsgHeader")}*`);
  lines.push(`#${shortId}`);
  lines.push("");
  lines.push(`*${t("orderSuccess.waItems")}:*`);
  (order.items || []).forEach((it) => {
    const variant = it.variant_label ? ` (${it.variant_label})` : "";
    lines.push(`• ${it.name}${variant} × ${it.quantity} — ${formatJOD(it.price * it.quantity)}`);
  });
  lines.push("");
  lines.push(`*${t("orderSuccess.waTotal")}:* ${formatJOD(order.total)}`);
  lines.push(`*${t("orderSuccess.waPayment")}:* ${t("orderSuccess.waPaymentText")}`);
  const s = order.shipping || {};
  lines.push("");
  lines.push(`*${t("orderSuccess.waShipTo")}:*`);
  lines.push(`${s.full_name || ""}`);
  if (s.phone) lines.push(`${s.phone}`);
  lines.push(`${s.address || ""}${s.city ? `, ${s.city}` : ""}`);
  if (s.notes) lines.push(`"${s.notes}"`);
  return lines.filter(Boolean).join("\n");
};

export default function OrderSuccess() {
  const { orderId } = useParams();
  const { t } = useLang();
  const { settings } = useSiteSettings();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    // Best-effort fetch — if it fails we still show the confirmation page
    // (order was created, customer just loses the pre-filled WA message).
    api
      .get(`/orders/${orderId}`)
      .then((r) => {
        setOrder(r.data);
        // Fire marketing pixel purchase event (Meta, GA4, Google Ads, TikTok, Snap)
        if (typeof window !== "undefined" && typeof window.__shamsFirePurchase === "function") {
          try {
            window.__shamsFirePurchase(
              r.data?.id || orderId,
              r.data?.total,
              r.data?.items || [],
            );
          } catch (e) {
            console.debug("purchase pixel fire failed:", e);
          }
        }
      })
      .catch(() => setOrder(null));
  }, [orderId]);

  const phone = (settings?.general?.whatsapp_number || "962799448817").replace(/[^\d]/g, "");
  const waMessage = buildWaMessage(order, t);
  const waUrl = `https://wa.me/${phone}?text=${encodeURIComponent(waMessage)}`;

  return (
    <div data-testid="order-success-page" className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 text-center">
      <CheckCircle size={64} className="mx-auto text-electric" />
      <h1 className="mt-6 font-display font-black text-4xl sm:text-5xl tracking-tighter">
        {t("orderSuccess.title")}
      </h1>
      <p className="mt-3 text-slate-600">
        {t("account.orderId")}:{" "}
        <span className="font-mono font-semibold">{orderId}</span>
      </p>
      <p className="mt-6 max-w-lg mx-auto text-slate-600">
        {t("orderSuccess.note")}
      </p>

      {/* WhatsApp CTA — hero action on this page */}
      {order && (
        <div className="mt-10 max-w-lg mx-auto bg-white rounded-2xl p-6 shadow-[0_4px_24px_-8px_rgba(0,0,0,0.12)] border border-black/5">
          <p className="text-sm text-slate-600">{t("orderSuccess.waHint")}</p>
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="order-success-whatsapp"
            className="mt-4 inline-flex items-center justify-center gap-2 h-14 px-8 rounded-full bg-[#25D366] text-white text-sm font-display font-bold tracking-wide hover:bg-[#1ebe5d] transition-colors shadow-[0_8px_24px_-8px_rgba(37,211,102,0.5)]"
          >
            <MessageCircle size={20} />
            {t("orderSuccess.waButton")}
          </a>
        </div>
      )}

      <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
        <Link
          to={`/track/${orderId}`}
          data-testid="order-success-track"
          className="h-11 px-6 inline-flex items-center justify-center rounded-full bg-ink text-white hover:bg-electric text-xs font-mono uppercase tracking-[0.2em]"
        >
          {t("orderSuccess.track")}
        </Link>
        <Link
          to="/shop"
          className="h-11 px-6 inline-flex items-center justify-center rounded-full bg-secondary hover:bg-ink hover:text-white text-xs font-mono uppercase tracking-[0.2em]"
        >
          {t("common.continueShopping")}
        </Link>
      </div>
    </div>
  );
}
